import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, Pagination } from '../components/common/UI';
import { toShamsi, toShamsiWithDay, isToday, daysUntil } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdAdd, MdEdit, MdDelete } from '../utils/icons';
import { MdApps, MdLocationOn, MdAccessTime, MdCheckCircle } from 'react-icons/md';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

const EVENT_STATUS = {
  Upcoming: { label: 'آینده', color: 'bg-blue-100 text-blue-700 border border-blue-200' },
  Today: { label: 'امروز', color: 'bg-orange-100 text-orange-700 border border-orange-200' },
  Completed: { label: 'انجام شده', color: 'bg-green-100 text-green-700 border border-green-200' },
  Cancelled: { label: 'لغو شده', color: 'bg-red-100 text-red-700 border border-red-200' },
};
const EVENT_TYPES = { Wedding: 'عروسی', Party: 'پارتی', Commercial: 'تبلیغاتی', Music: 'موزیک', Other: 'دیگر' };
const initForm = { project: '', title: '', type: 'Wedding', date: '', location: '', status: 'Upcoming', notes: '', assignedTeam: [], assignedEquipment: [] };

export default function Events() {
  const { isManager } = useAuth();
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [form, setForm] = useState(initForm);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');

  const load = useCallback(async () => {
    try {
      const [evRes, projRes, empRes, eqRes] = await Promise.all([
        API.get('/events'),
        API.get('/projects'),
        API.get('/employees'),
        API.get('/equipment'),
      ]);
      setEvents(evRes.data);
      setProjects(projRes.data);
      setEmployees(empRes.data);
      setEquipment(eqRes.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const tabFiltered = events.filter(e => {
    if (activeTab === 'upcoming') return e.status === 'Upcoming';
    if (activeTab === 'today') return isToday(e.date);
    if (activeTab === 'done') return e.status === 'Completed';
    return true;
  });

  // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [activeTab]);
  const totalPages = Math.max(1, Math.ceil(tabFiltered.length / PAGE_SIZE));
  const paged = tabFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const openAdd = () => { setForm(initForm); setEditItem(null); setShowModal(true); };
  const openEdit = (ev) => {
    setForm({
      project: ev.project?._id || '', title: ev.title, type: ev.type,
      date: ev.date?.split('T')[0] || '', location: ev.location,
      status: ev.status, notes: ev.notes || '',
      assignedTeam: ev.assignedTeam?.map(e => e._id) || [],
      assignedEquipment: ev.assignedEquipment?.map(e => e._id) || [],
    });
    setEditItem(ev); setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.date || !form.location) return toast.error('عنوان، تاریخ و لوکیشن ضروری است');
    setSaving(true);
    try {
      if (editItem) { await API.put(`/events/${editItem._id}`, form); toast.success('رویداد بروزرسانی شد'); }
      else { await API.post('/events', form); toast.success('رویداد اضافه شد'); }
      setShowModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'خطا'); } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await API.delete(`/events/${deleteId}`); toast.success('رویداد حذف شد'); setDeleteId(null); load(); }
    catch { toast.error('خطا'); }
  };

  if (loading) return <PageLoader />;

  const tabs = [
    { key: 'all', label: 'همه', count: events.length, icon: MdApps },
    { key: 'today', label: 'امروز', count: events.filter(e => isToday(e.date)).length, icon: MdLocationOn },
    { key: 'upcoming', label: 'آینده', count: events.filter(e => e.status === 'Upcoming').length, icon: MdAccessTime },
    { key: 'done', label: 'انجام شده', count: events.filter(e => e.status === 'Completed').length, icon: MdCheckCircle },
  ];

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader title="رویدادها" subtitle={`${events.length} رویداد`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="رویداد جدید" />} />

      <div className="flex gap-2 overflow-x-auto pb-1">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}>
            {tab.label}
            <span className={`text-xs px-1.5 py-0.5 rounded-md ${activeTab === tab.key ? 'bg-gray-200' : 'bg-gray-100'}`}>{tab.count}</span>
          </button>
        ))}
      </div>

      {tabFiltered.length === 0 ? (
        <EmptyState icon="📅" title="رویدادی یافت نشد"
          action={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="رویداد جدید" />} />
      ) : (
        <>
          {/* Desktop Table */}
          <div className="glass-card overflow-hidden hidden md:block">
            <div className="overflow-x-auto">
              <table className="premium-table">
                <thead>
                  <tr><th>عنوان</th><th>نوع</th><th>پروژه</th><th>تاریخ</th><th>لوکیشن</th><th>تیم</th><th>وضعیت</th><th>عملیات</th></tr>
                </thead>
                <tbody>
                  {paged.map(ev => {
                    const days = daysUntil(ev.date);
                    return (
                      <tr key={ev._id} className={isToday(ev.date) ? 'ring-1 ring-orange-500/30' : ''}>
                        <td>
                          <div className="font-semibold text-gray-800">{ev.title}</div>
                          {isToday(ev.date) && <div className="text-xs text-orange-600 mt-0.5">📍 امروز!</div>}
                        </td>
                        <td className="text-gray-600">{EVENT_TYPES[ev.type] || ev.type}</td>
                        <td className="text-gray-500 text-sm">{ev.project?.name || '---'}</td>
                        <td>
                          <div className="text-gray-600 text-sm">{toShamsi(ev.date)}</div>
                          {ev.status === 'Upcoming' && days >= 0 && (
                            <div className={`text-xs mt-0.5 ${days === 0 ? 'text-orange-600' : days <= 3 ? 'text-yellow-600' : 'text-gray-600'}`}>
                              {days === 0 ? 'امروز!' : `${days} روز دیگر`}
                            </div>
                          )}
                        </td>
                        <td className="text-gray-500">📍 {ev.location}</td>
                        <td>
                          <div className="flex flex-wrap gap-1">
                            {ev.assignedTeam?.slice(0, 2).map(e => (
                              <span key={e._id} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{e.name}</span>
                            ))}
                            {ev.assignedTeam?.length > 2 && <span className="text-xs text-gray-600">+{ev.assignedTeam.length - 2}</span>}
                          </div>
                        </td>
                        <td><Badge statusConfig={EVENT_STATUS[ev.status]} /></td>
                        <td>
                          <div className="flex items-center gap-1">
                            {isManager && <button onClick={() => openEdit(ev)} className="p-1.5 text-yellow-600 hover:text-yellow-700 hover:bg-yellow-100 rounded-lg transition-all"><MdEdit size={15} /></button>}
                            {isManager && <button onClick={() => setDeleteId(ev._id)} className="p-1.5 text-red-600 hover:text-red-700 hover:bg-red-100 rounded-lg transition-all"><MdDelete size={15} /></button>}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {paged.map(ev => (
              <div key={ev._id} className={`mobile-card ${isToday(ev.date) ? 'border-orange-200' : ''}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-semibold text-gray-800">{ev.title}</div>
                    <div className="text-xs text-gray-500 mt-0.5">{EVENT_TYPES[ev.type]} • {toShamsi(ev.date)}</div>
                  </div>
                  <Badge statusConfig={EVENT_STATUS[ev.status]} />
                </div>
                <div className="text-sm text-gray-500">📍 {ev.location}</div>
                {ev.assignedTeam?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {ev.assignedTeam.map(e => (
                      <span key={e._id} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-md">{e.name}</span>
                    ))}
                  </div>
                )}
                {isToday(ev.date) && <div className="text-xs text-orange-600 font-medium mt-2">🔔 این رویداد امروز است!</div>}
                {isManager && (
                  <div className="flex gap-2 mt-3">
                    <button onClick={() => openEdit(ev)} className="flex-1 py-1.5 text-xs bg-gray-50 text-gray-800 rounded-lg hover:bg-gray-100 transition-colors">ویرایش</button>
                    <button onClick={() => setDeleteId(ev._id)} className="flex-1 py-1.5 text-xs bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors">حذف</button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </>
      )}

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش رویداد' : 'رویداد جدید'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <FormField label="عنوان رویداد" required>
              <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="عنوان رویداد" />
            </FormField>
          </div>
          <FormField label="نوع">
            <select className="form-input" value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
              {Object.entries(EVENT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </FormField>
          <FormField label="پروژه مرتبط">
            <select className="form-input" value={form.project} onChange={e => setForm({ ...form, project: e.target.value })}>
              <option value="">انتخاب پروژه (اختیاری)</option>
              {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
            </select>
          </FormField>
          <FormField label="تاریخ" required>
            <ShamsiDatePicker value={form.date} onChange={date => setForm({ ...form, date: date })} className="form-input" />
          </FormField>
          <FormField label="لوکیشن" required>
            <input className="form-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="آدرس محل رویداد" />
          </FormField>
          <FormField label="وضعیت">
            <select className="form-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
              {Object.entries(EVENT_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </FormField>
          <FormField label="تیم assigned">
            <select multiple className="form-input h-28" value={form.assignedTeam}
              onChange={e => setForm({ ...form, assignedTeam: Array.from(e.target.selectedOptions, o => o.value) })}>
              {employees.filter(e => e.status !== 'Inactive').map(e => <option key={e._id} value={e._id}>{e.name} - {e.role}</option>)}
            </select>
            <p className="text-xs text-gray-600 mt-1">Ctrl را نگه دارید برای چند انتخاب</p>
          </FormField>
          <FormField label="تجهیزات">
            <select multiple className="form-input h-28" value={form.assignedEquipment}
              onChange={e => setForm({ ...form, assignedEquipment: Array.from(e.target.selectedOptions, o => o.value) })}>
              {equipment.filter(eq => eq.status !== 'Maintenance').map(eq => <option key={eq._id} value={eq._id}>{eq.name} ({eq.type})</option>)}
            </select>
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="یادداشت">
              <textarea className="form-input h-20 resize-none" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="یادداشت اختیاری..." />
            </FormField>
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-sm">انصراف</button>
          <ActionBtn onClick={handleSave} label={saving ? 'ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
        </div>
      </Modal>

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف رویداد" message="آیا مطمئن هستید؟" />
    </div>
  );
}
